import { ActionIcon, Button, Loader, Tooltip } from "@mantine/core";
import { X } from "lucide-react";
import { useState } from "react";

import type { PickedImage } from "../../../platform/media/ImagePicker";
import classes from "./ad-copies.module.css";
import { InspirationPopover } from "./InspirationPopover";
import { ANALYSING } from "./use-ad-structures";

export interface InspirationFieldProps {
  teamId: string;
  brandId: string;
  inspiration: PickedImage | null;
  /** Its analysis runs: the picture is washed out under a spinner, with live's "Analysing...". */
  analysing: boolean;
  disabled: boolean;
  onPick: (image: PickedImage) => void;
  onRemove: () => void;
}

/**
 * AI Generate's "Inspiration" (live's `ReferenceSelectPopover`, captures advanced-234/05 and 22):
 * the picture with an x and "Change", or an empty picture and "Add image"; either opens live's
 * "Add inspiration".
 */
export function InspirationField(props: InspirationFieldProps) {
  const { teamId, brandId, inspiration, analysing, disabled, onPick, onRemove } = props;
  const [opened, setOpened] = useState(false);
  return (
    <div className={classes.productField}>
      {inspiration === null || inspiration.url === null ? (
        <span className={classes.productPicture} aria-hidden />
      ) : (
        <Tooltip label={ANALYSING} withArrow disabled={!analysing}>
          <span className={classes.fieldPicture} data-analysing={analysing || undefined}>
            <img src={inspiration.url} alt={inspiration.name} className={classes.productPicture} />
            {analysing ? (
              <span className={classes.fieldLoader}>
                <Loader size="sm" aria-label={ANALYSING} />
              </span>
            ) : null}
            <ActionIcon
              variant="default"
              size="sm"
              radius="xl"
              className={classes.fieldRemove}
              aria-label="Remove the inspiration"
              disabled={disabled}
              onClick={onRemove}
            >
              <X size={12} aria-hidden />
            </ActionIcon>
          </span>
        </Tooltip>
      )}
      <InspirationPopover
        teamId={teamId}
        brandId={brandId}
        chosenId={inspiration?.assetId ?? null}
        opened={opened}
        onClose={() => setOpened(false)}
        onPick={onPick}
        anchor={(wrap) =>
          wrap(
            <Button
              variant="default"
              disabled={disabled}
              onClick={() => setOpened((open) => !open)}
            >
              {inspiration === null ? "Add image" : "Change"}
            </Button>,
          )
        }
      />
    </div>
  );
}
